import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { HiOutlineTrash, HiOutlineChevronUp, HiOutlineChevronDown, HiOutlineX, HiOutlineDotsVertical, HiOutlineClipboardCopy } from "react-icons/hi";
import { HiRefresh, HiDownload } from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { QRCodeCanvas } from "qrcode.react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import { APP_URL } from "../../config";
import { capture } from "../../instrument";
import { INPUT_CLASS_NAME } from "../../utils";
import { useHotkeys } from "react-hotkeys-hook";
import useRecordNavShortcuts from "../../hooks/useRecordNavShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";

const EditLaboratory = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isDirtyRef = useRef(false);
  const markDirty = () => {
    isDirtyRef.current = true;
  };

  const filteredIds = location.state?.filteredIds || null;
  const currentIndex = useMemo(() => (filteredIds ? filteredIds.indexOf(id) : -1), [filteredIds, id]);
  const hasNavigation = filteredIds !== null && currentIndex !== -1;

  const preview = useMemo(() => {
    const cachedLaboratories = queryClient.getQueryData(["laboratories"]);
    if (!cachedLaboratories) return null;
    const cached = cachedLaboratories.find((l) => l._id === id);
    return cached ? { name: cached.name, address: cached.address, hprimCode: cached.hprimCode } : null;
  }, [id]);

  const navigateTo = (index) => {
    const targetId = filteredIds[index];
    history.replace({ pathname: `/laboratory/${targetId}/edit`, state: { filteredIds } });
  };

  const navigatePrev = () => {
    if (!hasNavigation || currentIndex <= 0) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex - 1);
  };

  const navigateNext = () => {
    if (!hasNavigation || currentIndex >= filteredIds.length - 1) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex + 1);
  };

  const goBack = () => {
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    history.push("/laboratory");
  };

  useRecordNavShortcuts(
    {
      onPrev: navigatePrev,
      onNext: navigateNext,
      onBack: goBack,
      canPrev: hasNavigation && currentIndex > 0,
      canNext: hasNavigation && currentIndex < filteredIds?.length - 1,
    },
    [hasNavigation, currentIndex, filteredIds],
  );

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [laboratoryCode, setLaboratoryCode] = useState("");
  const [hprimCode, setHprimCode] = useState("");
  const [samplerCode, setSamplerCode] = useState("");
  const [coordinates, setCoordinates] = useState([0, 0]);
  const [homePageMessage, setHomePageMessage] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  const [qrCodeToken, setQrCodeToken] = useState("");
  const [botIds, setBotIds] = useState([]);
  const [displayMode, setDisplayMode] = useState("laboratory");

  const {
    data: laboratoryData,
    isLoading: isLoadingData,
    isError,
  } = useQuery({
    queryKey: ["laboratory", id],
    queryFn: async () => {
      const response = await API.get(`/admin/laboratory/${id}`);
      if (!response.ok) throw new Error(response.message || "Impossible de charger le laboratoire");
      return response.data;
    },
    staleTime: Infinity,
  });

  const { data: machines = [] } = useQuery({
    queryKey: ["machines"],
    queryFn: async () => {
      const response = await API.get("/machine");
      return response.data;
    },
    select: (allMachines) => allMachines.filter((machine) => machine.kind !== "agent"),
  });

  useEffect(() => {
    if (!laboratoryData) return;
    setName(laboratoryData.name);
    setAddress(laboratoryData.address);
    setPhone(laboratoryData.phone || "");
    setLaboratoryCode(laboratoryData.laboratoryCode || "");
    setHprimCode(laboratoryData.hprimCode || "");
    setSamplerCode(laboratoryData.samplerCode || "");
    setCoordinates(laboratoryData.location?.coordinates || [0, 0]);
    setHomePageMessage(laboratoryData.homePageMessage || "");
    setIsVisible(laboratoryData.isVisible !== false);
    setQrCodeToken(laboratoryData.qrCodeToken || "");
    setBotIds(laboratoryData.botIds || []);
    isDirtyRef.current = false;
  }, [laboratoryData]);

  useEffect(() => {
    if (!isError) return;
    toast.error("Une erreur est survenue lors du chargement du laboratoire");
    history.push("/laboratory");
  }, [isError]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateMutation = useMutation({
    mutationFn: (body) => API.put(`/admin/laboratory/${id}`, body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      isDirtyRef.current = false;
      toast.success("Laboratoire mis à jour avec succès");
      queryClient.invalidateQueries({ queryKey: ["laboratories"] });
      queryClient.invalidateQueries({ queryKey: ["laboratory", id] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => API.delete(`/admin/laboratory/${id}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Laboratoire supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["laboratories"] });
      history.push("/laboratory");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const regenerateQRMutation = useMutation({
    mutationFn: () => API.put(`/admin/laboratory/${id}/regenerate-qr`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      setQrCodeToken(response.data.qrCodeToken || "");
      toast.success("QR Code régénéré avec succès");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const isMutating = updateMutation.isPending || deleteMutation.isPending || regenerateQRMutation.isPending;

  const updateLaboratory = () => {
    if (!name || !address) return toast.error("Veuillez remplir tous les champs obligatoires");
    updateMutation.mutate({
      name,
      address,
      phone,
      laboratoryCode,
      hprimCode,
      samplerCode,
      homePageMessage,
      isVisible,
      botIds,
      location: { type: "Point", coordinates },
    });
  };

  const onToggleBot = (botId) => {
    setBotIds(botIds.includes(botId) ? botIds.filter((b) => b !== botId) : [...botIds, botId]);
  };

  useHotkeys(SHORTCUTS.EDIT_SAVE.keys, () => updateLaboratory(), { preventDefault: true, enableOnFormTags: true, enabled: !isMutating }, [updateLaboratory, isMutating]);

  const deleteLaboratory = () => {
    const confirm = window.confirm("Êtes-vous sûr de vouloir supprimer ce laboratoire ?");
    if (!confirm) return;
    deleteMutation.mutate();
  };

  const regenerateQRCode = () => {
    const confirm = window.confirm(
      "Attention : l'ancien QR Code sera automatiquement désactivé. Il faudra remplacer l'ensemble des anciens QR Codes présents dans le laboratoire par le nouveau. Voulez-vous continuer ?",
    );
    if (!confirm) return;
    regenerateQRMutation.mutate();
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById("qr-code-canvas");
    if (!canvas) return toast.error("Il faut d'abord générer un QR Code");

    const padding = 20;
    const newCanvas = document.createElement("canvas");
    const newContext = newCanvas.getContext("2d");

    newCanvas.width = canvas.width + padding * 2;
    newCanvas.height = canvas.height + padding * 2;

    newContext.fillStyle = "white";
    newContext.fillRect(0, 0, newCanvas.width, newCanvas.height);
    newContext.drawImage(canvas, padding, padding);

    const url = newCanvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.download = `${displayMode === "laboratory" ? "laboratoire-" : "domicile-"}qrcode-${name}.png`;
    link.href = url;
    link.click();
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      {hasNavigation && (
        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-1.5">
          <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={goBack} title="Fermer (Esc)">
            <HiOutlineX className="h-4 w-4" />
          </button>
          <div className="mx-0.5 h-4 w-px bg-gray-200" />
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigatePrev}
            disabled={currentIndex <= 0}
          >
            <HiOutlineChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigateNext}
            disabled={currentIndex >= filteredIds.length - 1}
          >
            <HiOutlineChevronDown className="h-4 w-4" />
          </button>
          <span className="ml-1 text-xs tabular-nums text-gray-500">
            {currentIndex + 1} / {filteredIds.length}
          </span>
        </div>
      )}

      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="flex items-center gap-3">
          <h1 className="truncate text-lg font-semibold text-gray-900">{laboratoryData?.name || preview?.name || "Laboratoire"}</h1>
          {(laboratoryData?.hprimCode || preview?.hprimCode) && (
            <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{laboratoryData?.hprimCode || preview?.hprimCode}</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <div className="relative" ref={menuRef}>
            <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <HiOutlineDotsVertical className="h-5 w-5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.open(`/audit-log/laboratories/${id}`, "_blank", "noopener,noreferrer,width=1000,height=800");
                  }}
                >
                  <PiClockCounterClockwise className="h-4 w-4" />
                  Historique
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-b-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    deleteLaboratory();
                  }}
                  disabled={isMutating}
                >
                  <HiOutlineTrash className="h-4 w-4" />
                  Supprimer
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={() => {
              updateLaboratory();
              notifyShortcut("EDIT_SAVE");
            }}
            disabled={isMutating || isLoadingData}
          >
            {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {isLoadingData ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-5 overflow-hidden">
          <div className="col-span-3 flex-1 overflow-y-auto" onChangeCapture={markDirty}>
            <div className="mx-auto max-w-4xl px-8 py-6">
              <h3 className="mb-4 text-lg font-medium text-gray-900">Détails</h3>

              <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Nom*</label>
                  <input className={INPUT_CLASS_NAME} placeholder="Nom du laboratoire" value={name} onChange={(e) => setName(e.target.value)} disabled={isMutating} />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Adresse*</label>
                  <input className={INPUT_CLASS_NAME} placeholder="Adresse" value={address} onChange={(e) => setAddress(e.target.value)} disabled={isMutating} />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Téléphone</label>
                  <input className={INPUT_CLASS_NAME} placeholder="Téléphone" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={isMutating} />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Code HRPIM</label>
                  <input className={INPUT_CLASS_NAME} placeholder="Code HRPIM" value={hprimCode} onChange={(e) => setHprimCode(e.target.value)} disabled={isMutating} />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Code laboratoire</label>
                  <input
                    className={INPUT_CLASS_NAME}
                    placeholder="Code laboratoire"
                    value={laboratoryCode}
                    onChange={(e) => setLaboratoryCode(e.target.value)}
                    disabled={isMutating}
                  />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Code de préleveur</label>
                  <input className={INPUT_CLASS_NAME} placeholder="Code de préleveur" value={samplerCode} onChange={(e) => setSamplerCode(e.target.value)} disabled={isMutating} />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Latitude</label>
                  <input
                    type="number"
                    className={INPUT_CLASS_NAME}
                    placeholder="Latitude"
                    value={coordinates[1]}
                    onChange={(e) => setCoordinates([coordinates[0], parseFloat(e.target.value) || 0])}
                    disabled={isMutating}
                  />
                </div>
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 text-[13px] font-medium text-gray-600">Longitude</label>
                  <input
                    type="number"
                    className={INPUT_CLASS_NAME}
                    placeholder="Longitude"
                    value={coordinates[0]}
                    onChange={(e) => setCoordinates([parseFloat(e.target.value) || 0, coordinates[1]])}
                    disabled={isMutating}
                  />
                </div>
                <div className="flex items-start gap-4 px-5 py-3.5">
                  <label className="w-64 shrink-0 pt-2 text-[13px] font-medium text-gray-600">Message page d'accueil</label>
                  <textarea
                    className={`${INPUT_CLASS_NAME} min-h-[100px] resize-y`}
                    placeholder="Texte affiché sur la page d'accueil de l'app pour ce laboratoire (optionnel)"
                    value={homePageMessage}
                    maxLength={4000}
                    onChange={(e) => setHomePageMessage(e.target.value)}
                    disabled={isMutating}
                  />
                </div>
                <div className="flex items-center justify-between gap-4 px-5 py-3.5">
                  <div className="w-64 shrink-0">
                    <span className="text-[13px] font-medium text-gray-600">Visible sur l'app patient</span>
                    <span className="block text-xs text-gray-500">Affiché dans le sélecteur de laboratoire</span>
                  </div>
                  <button
                    type="button"
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${isVisible ? "bg-primary-500" : "bg-gray-300"}`}
                    onClick={() => {
                      markDirty();
                      setIsVisible(!isVisible);
                    }}
                    disabled={isMutating}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isVisible ? "translate-x-5" : "translate-x-0"}`}
                    />
                  </button>
                </div>
              </div>

              <h3 className="mb-4 mt-8 text-lg font-medium text-gray-900">Machines</h3>

              <div className="rounded-lg border border-gray-100 bg-white">
                <p className="border-b border-gray-100 px-5 py-3.5 text-xs text-gray-500">
                  Les admissions de ce laboratoire ne sont enregistrées que par les machines sélectionnées. Sans sélection, toutes les machines peuvent les traiter.
                </p>
                {machines.length === 0 && botIds.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">Aucune machine connue</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {machines.map((machine) => (
                      <label key={machine.botId} className="flex cursor-pointer items-center gap-3 px-5 py-3 text-sm hover:bg-gray-50">
                        <input
                          type="checkbox"
                          name={`bot-${machine.botId}`}
                          checked={botIds.includes(machine.botId)}
                          onChange={() => onToggleBot(machine.botId)}
                          disabled={isMutating}
                          className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        />
                        <span className="flex-1 truncate font-medium text-gray-900">{machine.botId}</span>
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                          <span className={`h-1.5 w-1.5 rounded-full ${machine.online ? "bg-emerald-500" : "bg-gray-300"}`} />
                          {machine.online ? "En ligne" : "Hors ligne"}
                        </span>
                      </label>
                    ))}
                    {botIds
                      .filter((botId) => machines.some((machine) => machine.botId === botId) === false)
                      .map((botId) => (
                        <label key={botId} className="flex cursor-pointer items-center gap-3 px-5 py-3 text-sm hover:bg-gray-50">
                          <input
                            type="checkbox"
                            name={`bot-${botId}`}
                            checked
                            onChange={() => onToggleBot(botId)}
                            disabled={isMutating}
                            className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                          />
                          <span className="flex-1 truncate font-medium text-gray-900">{botId}</span>
                          <span className="text-xs text-gray-400">Machine inconnue</span>
                        </label>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-lg font-medium text-gray-900">Lien</h3>
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                <button
                  type="button"
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${displayMode === "laboratory" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  onClick={() => setDisplayMode("laboratory")}
                >
                  Au labo
                </button>
                <button
                  type="button"
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${displayMode === "home" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  onClick={() => setDisplayMode("home")}
                >
                  À domicile
                </button>
              </div>
            </div>
            <div className="rounded-lg border border-gray-100 p-4">
              {displayMode === "laboratory" ? (
                <>
                  <div className="mb-4 flex items-center gap-3">
                    <button className="flex items-center text-sm text-primary-500 hover:text-primary-600" onClick={regenerateQRCode} disabled={isMutating}>
                      <HiRefresh className="mr-1" />
                      Régénérer
                    </button>
                    {qrCodeToken && (
                      <button className="flex items-center text-sm text-primary-500 hover:text-primary-600" onClick={downloadQRCode}>
                        <HiDownload className="mr-1" />
                        Télécharger
                      </button>
                    )}
                  </div>
                  {qrCodeToken ? (
                    <div className="flex flex-col items-center gap-4">
                      <QRCodeCanvas id="qr-code-canvas" value={`${APP_URL}/?laboratory=${id}&laboratoryToken=${qrCodeToken}`} size={200} />
                      <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                        <span className="flex-1 truncate font-mono text-xs text-gray-600">{`${APP_URL}/?laboratory=${id}&laboratoryToken=${qrCodeToken}`}</span>
                        <button
                          type="button"
                          className="shrink-0 rounded-md p-1.5 text-primary-500 transition-colors hover:bg-primary-50 hover:text-primary-600"
                          onClick={() => {
                            navigator.clipboard.writeText(`${APP_URL}/?laboratory=${id}&laboratoryToken=${qrCodeToken}`);
                            toast.success("Lien copié");
                          }}
                          title="Copier le lien"
                        >
                          <HiOutlineClipboardCopy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8 text-sm text-gray-400">Aucun QR Code généré</div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-4 flex items-center gap-3">
                    <button className="flex items-center text-sm text-primary-500 hover:text-primary-600" onClick={downloadQRCode}>
                      <HiDownload className="mr-1" />
                      Télécharger
                    </button>
                  </div>
                  <div className="flex flex-col items-center gap-4">
                    <QRCodeCanvas id="qr-code-canvas" value={`${APP_URL}/?laboratory=${id}`} size={200} />
                    <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                      <span className="flex-1 truncate font-mono text-xs text-gray-600">{`${APP_URL}/?laboratory=${id}`}</span>
                      <button
                        type="button"
                        className="shrink-0 rounded-md p-1.5 text-primary-500 transition-colors hover:bg-primary-50 hover:text-primary-600"
                        onClick={() => {
                          navigator.clipboard.writeText(`${APP_URL}/?laboratory=${id}`);
                          toast.success("Lien copié");
                        }}
                        title="Copier le lien"
                      >
                        <HiOutlineClipboardCopy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditLaboratory;
